import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getSimplifiedError } from "../../utils";
import { ApiService, url } from "../../utils/endpoints";
import { handleRejected, startLoading } from "../Authentication/authSlice";
import { toast } from "sonner";

const block = {
  loading: false,
  success: false,
  error: { status: null, message: "" },
  data: {},
};

const initialState = {
  ...block,
  editCreatorsPageState: { ...block },
  introVideoState: { ...block },
  publishState: { ...block },
};

const handleFulfilled = (
  state: any,
  initialStateKey: string,
  { payload }: { payload: any }
) => {
  if (initialStateKey === null) {
    state.loading = false;
    state.success = true;
    state.data = payload?.data;
  } else if (state.hasOwnProperty(initialStateKey)) {
    state[initialStateKey].loading = false;
    state[initialStateKey].success = true;
    state[initialStateKey].data = {
      ...payload.data,
    };
  }
};

export const getCreatorsPage = createAsyncThunk(
  "creatorspage",
  async (payload: any, { rejectWithValue, getState }) => {
    try {
      const user: any = getState();
      const { data } = await ApiService.get(url.creatorspage, {
        headers: { Authorization: `Bearer ${user?.user?.token}` },
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(getSimplifiedError(error));
    }
  }
);

export const updateCreatorsPage = createAsyncThunk(
  "updateCreatorsPage",
  async (payload: any, { rejectWithValue, getState }) => {
    try {
      const user: any = getState();
      const { data } = await ApiService.put(url.creatorspage, payload, {
        headers: { Authorization: `Bearer ${user?.user?.token}` },
      });
      toast.success("Your page settings updated successfully");
      return data;
    } catch (error: any) {
      return rejectWithValue(getSimplifiedError(error));
    }
  }
);

export const updateCreatorsIntroVideo = createAsyncThunk(
  "updateCreatorsIntroVideo",
  async (payload: any, { rejectWithValue, getState }) => {
    try {
      const user: any = getState();
      const { data } = await ApiService.put(
        `${url.creatorspage}/intro-video`,
        payload,
        {
          headers: { Authorization: `Bearer ${user?.user?.token}` },
        }
      );
      toast.success("Intro video uploaded successfully");
      return data;
    } catch (error: any) {
      return rejectWithValue(getSimplifiedError(error));
    }
  }
);

export const handlePublish = createAsyncThunk(
  "publishPage",
  async (payload: any, { rejectWithValue, getState }) => {
    try {
      const user: any = getState();
      const { data } = await ApiService.post(
        `${url.creatorspage}/${payload}`,
        "",
        {
          headers: { Authorization: `Bearer ${user?.user?.token}` },
        }
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(getSimplifiedError(error));
    }
  }
);

export const creatorPageSlice = createSlice({
  name: "creatorsPage",
  initialState,
  reducers: {
    clearState: (state) => {
      Object.assign(state, initialState);
    },
    resetBlockAuth(state, action: PayloadAction<{ blockType: any }>) {
      return action.payload.blockType === null
        ? { ...state, loading: false, success: false }
        : {
            ...state,
            [action.payload.blockType]: {
              ...initialState[action.payload.blockType],
            },
          };
    },
    resetFlag(state, action: PayloadAction<{ blockType: string }>) {
      const { blockType } = action.payload;
      const initialStateForKey = initialState[blockType];
      if (blockType === null) {
        return {
          ...initialState,
          data: state.data, // Retain data
        };
      }
      return {
        ...state,
        [blockType]: {
          ...state[blockType],
          loading: initialStateForKey.loading,
          success: initialStateForKey.success,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCreatorsPage.pending, (state, action) => {
        startLoading(state, null, { loading: true, success: false });
      })
      .addCase(getCreatorsPage.fulfilled, (state, action) => {
        handleFulfilled(state, null, action);
      })
      .addCase(getCreatorsPage.rejected, (state, action) => {
        handleRejected(state, null, action);
      })
      .addCase(updateCreatorsPage.pending, (state, action) => {
        startLoading(state, "editCreatorsPageState", {
          loading: true,
          success: false,
        });
      })
      .addCase(updateCreatorsPage.fulfilled, (state, action) => {
        handleFulfilled(state, "editCreatorsPageState", action);
      })
      .addCase(updateCreatorsPage.rejected, (state, action) => {
        handleRejected(state, "editCreatorsPageState", action);
      })
      .addCase(updateCreatorsIntroVideo.pending, (state, action) => {
        startLoading(state, "introVideoState", {
          loading: true,
          success: false,
        });
      })
      .addCase(updateCreatorsIntroVideo?.fulfilled, (state, action) => {
        handleFulfilled(state, "introVideoState", action);
      })
      .addCase(updateCreatorsIntroVideo?.rejected, (state, action) => {
        handleRejected(state, "introVideoState", action);
      })
      .addCase(handlePublish.pending, (state, action) => {
        startLoading(state, "publishState", { loading: true, success: false });
      })
      .addCase(handlePublish.fulfilled, (state, action) => {
        handleFulfilled(state, "publishState", action);
      })
      .addCase(handlePublish.rejected, (state, action) => {
        handleRejected(state, "publishState", action);
      });
  },
});

export const creatorsPageSelector = (state: any) => state.creatorsPage;
export const { clearState, resetBlockAuth, resetFlag } =
  creatorPageSlice.actions;

export default creatorPageSlice.reducer;
